/** One labelled data point shared by the chart and its text-alternative table. */
export interface ChartDatum {
  label: string;
  value: number;
  /**
   * Marks the emphasized ("today") bar — rendered in full accent (chart-1) with
   * a dark-mode accent glow (ATLAS-05). Regular bars alternate chart-2/chart-3.
   */
  emphasized?: boolean;
}

/** Caller-supplied (i18n-resolved) labels — the package stays free of react-i18next (D-20). */
export interface ChartDataTableLabels {
  /** Accessible caption + table name, e.g. "Data table for Page views" (metric already interpolated). */
  caption: string;
  /** Header for the label/category column. */
  columnHeader: string;
  /** Header for the value column. */
  valueHeader: string;
}

export interface ChartDataTableProps {
  rows: ChartDatum[];
  labels: ChartDataTableLabels;
}

/**
 * The MANDATORY visually-hidden text alternative for a chart (RESEARCH Pitfall 6,
 * UI-SPEC Accessibility, WCAG SC 1.1.1 Non-text Content). vitest-axe does NOT
 * catch a missing text alternative for an SVG chart, so every MetricChart renders
 * this sibling table conveying the exact same numbers.
 *
 * The table is `sr-only` (present in the accessibility tree, visually hidden) and
 * is named via the caller-supplied `labels.caption` ("Data table for {metric}").
 */
export function ChartDataTable({ rows, labels }: ChartDataTableProps) {
  return (
    <div className="sr-only">
      <table aria-label={labels.caption}>
        <caption>{labels.caption}</caption>
        <thead>
          <tr>
            <th scope="col">{labels.columnHeader}</th>
            <th scope="col">{labels.valueHeader}</th>
          </tr>
        </thead>
        <tbody>
          {rows.map((row, i) => (
            <tr key={`${row.label}-${i}`}>
              <th scope="row">{row.label}</th>
              <td>{row.value.toLocaleString()}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}
